/* Owns the hover/focus tooltip the kit uses for `disabledReason` and for icon
   buttons. A permission-gated control is rendered DISABLED WITH A REASON rather
   than hidden (PANEL.md §3), so the tooltip is load-bearing: it is the only
   place the user learns why a button does nothing. */

import { useId, useState, type ReactNode } from 'react'

import { cn } from './cn.js'

export interface TooltipProps {
  /** The tooltip text. When empty the wrapper renders the child untouched. */
  content?: string
  children: (props: { 'aria-describedby': string | undefined }) => ReactNode
  side?: 'top' | 'bottom'
  className?: string
}

export function Tooltip({ content, children, side = 'top', className }: TooltipProps): ReactNode {
  const id = useId()
  const [open, setOpen] = useState(false)

  if (!content) return children({ 'aria-describedby': undefined })

  return (
    <span
      className={cn('relative inline-flex', className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={() => setOpen(false)}
    >
      {children({ 'aria-describedby': id })}
      {/* Always in the DOM so `aria-describedby` resolves even while hidden. */}
      <span
        id={id}
        role="tooltip"
        className={cn(
          'pointer-events-none absolute left-1/2 z-50 w-max max-w-64 -translate-x-1/2 rounded-md border border-border-strong bg-elevated px-2 py-1 text-xs text-text shadow-pop',
          side === 'top' ? 'bottom-full mb-1' : 'top-full mt-1',
          open ? 'opacity-100' : 'sr-only opacity-0',
        )}
      >
        {content}
      </span>
    </span>
  )
}
